import { useEffect, useState } from "react";
import { axiosInstance } from "../constants";
import {
  Loader2,
  RefreshCw,
  Users,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  Search,
} from "lucide-react";
import { Alert, Tooltip, Chip } from "@mui/material";

interface Contact {
  _id: string;
  consent: boolean;
  name: string;
  prenom: string;
  email: string;
  phoneNumber: string;
  sujet: string;
  message: string;
}

interface AccordionItemProps {
  title: string;
  content: string;
}

const AccordionItem = ({ title, content }: AccordionItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg mb-2">
      <button
        className="flex items-center justify-between w-full py-3 px-4 text-left font-medium text-gray-700 hover:bg-gray-50 transition-colors rounded-lg"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="truncate max-w-xs text-sm">{title}</span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 flex-shrink-0 text-indigo-500" />
        ) : (
          <ChevronDown className="w-4 h-4 flex-shrink-0 text-indigo-500" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg">
          <p className="whitespace-pre-wrap text-sm text-gray-600">{content}</p>
        </div>
      )}
    </div>
  );
};

const AdminComponent = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [activeTab, setActiveTab] = useState<"contacts">("contacts");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchContacts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get<Contact[]>("/contact/api");
      if (response.data) {
        setContacts(response.data);
      }
    } catch (error) {
      setError("Failed to fetch contacts. Please try again later.");
      console.error("Error fetching contacts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = async (tab: "contacts") => {
    setActiveTab(tab);
    if (tab === "contacts" && contacts.length === 0) {
      await fetchContacts();
    }
  };

  const handleRefresh = () => {
    fetchContacts();
  };

  const filteredContacts = contacts.filter(
    (contact) =>
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.sujet.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    fetchContacts();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Admin Dashboard
              </h1>
              <p className="text-gray-600 mt-1">
                Manage contacts and inquiries
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              {
                id: "contacts",
                label: "Contacts",
                icon: Users,
                count: contacts.length,
              },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id as any)}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? "bg-indigo-100 text-indigo-700 border-indigo-200"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`}
              >
                <tab.icon className="w-5 h-5 mr-2" />
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-indigo-100 bg-indigo-600 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {error && (
          <Alert severity="error" className="mb-6">
            {error}
          </Alert>
        )}

        {activeTab === "contacts" && (
          <div className="space-y-6">
            {/* Search and Filter Bar */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search contacts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contacts Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {isLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Subject
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Message
                        </th>

                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredContacts.length === 0 ? (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-6 py-12 text-center text-gray-500"
                          >
                            <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                            <p className="text-lg font-medium">
                              No contacts found
                            </p>
                            <p className="text-sm">
                              Try adjusting your search criteria
                            </p>
                          </td>
                        </tr>
                      ) : (
                        filteredContacts.map((contact) => (
                          <tr key={contact._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                  <Users className="w-5 h-5 text-indigo-600" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {contact.name} {contact.prenom}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {contact.email}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {contact.phoneNumber}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 max-w-[150px]">
                              <Chip
                                label={contact.sujet}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </td>
                            <td className="px-6 py-4">
                              <AccordionItem
                                title={
                                  contact.message.length > 50
                                    ? `${contact.message.substring(0, 50)}...`
                                    : contact.message
                                }
                                content={contact.message}
                              />
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <Tooltip title="Send email">
                                  <a
                                    href={`mailto:${contact.email}`}
                                    className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-100"
                                  >
                                    <Mail className="w-4 h-4" />
                                  </a>
                                </Tooltip>
                                <Tooltip title="Call">
                                  <a
                                    href={`tel:${contact.phoneNumber}`}
                                    className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-100"
                                  >
                                    <Phone className="w-4 h-4" />
                                  </a>
                                </Tooltip>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer Stats */}
        <div className="mt-8 text-sm text-gray-500 text-center bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <div className="flex justify-center space-x-8">
            <span>Total Contacts: {contacts.length}</span>
            <span>Last Updated: {new Date().toLocaleTimeString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminComponent;
